import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { banco } from '../../db/banco'
import type { SubPedido } from '../../db/tipos'
import { formatarMoeda, formatarData } from '../../lib/formatos'
import { ultimaAlteracaoDoPedido } from '../../pedidos/ultimaAlteracao'
import { normalizarTexto } from '../../lib/textoBR'
import { dataISOLocal } from '../../pedidos/precos'
import { limitesDoMes, dataBRDoTexto } from '../../lib/periodo'
import {
  estagioDoPedido, ORDEM_ESTAGIO, ROTULO_ESTAGIO, CLASSE_ESTAGIO,
  CLASSE_CARTAO_ESTAGIO, type Estagio,
} from '../../pedidos/estagio'
import { filtroDoEndereco, type FiltroLista } from '../../pedidos/filtroDoEndereco'

// Reforma visual de 17/09 (mockup dela). SVG inline no mesmo estilo ja usado
// em TelaPedido.tsx -- sem biblioteca de icones nova.
function IconeBusca() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  )
}

function IconeFiltro() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="7" y1="12" x2="17" y2="12" />
      <line x1="10" y1="18" x2="14" y2="18" />
    </svg>
  )
}

function IconeDocumento() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
      <path d="M14 2v6h6" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="16" y2="17" />
    </svg>
  )
}

function IconeCalendario() {
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  )
}

function IconeRelogio() {
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <polyline points="12 7 12 12 15.5 14" />
    </svg>
  )
}

function IconeCheck() {
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}

function IconeX() {
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  )
}

// Um icone por situacao: pendente (espera acao), feito (avancou) ou cancelado.
// Nao inventa um icone por situacao (seriam 7) -- agrupa pelo que a cor ja diz.
function IconeDoEstagio({ estagio }: { estagio: Estagio }) {
  if (estagio === 'cancelado') return <IconeX />
  if (estagio === 'rascunho' || estagio === 'fechado') return <IconeRelogio />
  return <IconeCheck />
}

// Cor SOLIDA do circulo do icone, uma por situacao -- pedido dela em 17/09
// ("coloca cores como na foto"). ⚠ Mesma matiz de CLASSE_ESTAGIO (o rotulo
// text/bg claro usado na pilula da lista), so mais forte: os dois tem de
// combinar, senao a MESMA situacao muda de cor entre o cartao de resumo e a
// pilula da linha logo abaixo -- e exatamente o que confunde de relance.
const COR_SOLIDA_ESTAGIO: Record<Estagio, string> = {
  rascunho: 'bg-slate-500',
  fechado: 'bg-amber-500',
  enviado: 'bg-purple-600',
  faturado: 'bg-blue-600',
  entregue: 'bg-teal-600',
  pago: 'bg-emerald-600',
  cancelado: 'bg-red-600',
}

// Os 4 cartoes de resumo do mockup (Total + os 3 que mais pedem giro no dia a
// dia). As outras 4 situacoes reais (Faturado/Entregue/Pago/Cancelado)
// continuam no filtro do icone, so nao ganham cartao proprio -- pedido dela,
// 17/09, pra nao lotar a tela.
const ESTAGIOS_COM_CARTAO: ReadonlyArray<Estagio> = ['enviado', 'rascunho', 'fechado']


export default function ListaPedidos() {
  const [filtro, setFiltro] = useState<FiltroLista>(() => filtroDoEndereco(window.location.search))
  const [busca, setBusca] = useState('')
  const [painelAberto, setPainelAberto] = useState(false)
  const [abaPainel, setAbaPainel] = useState<'situacao' | 'periodo'>('situacao')
  const [personalizadoDe, setPersonalizadoDe] = useState('')
  const [personalizadoAte, setPersonalizadoAte] = useState('')

  const pedidos = useLiveQuery(
    () => banco.pedidos.filter((p) => p.apagado_em === null).toArray(),
    [],
  )
  const clientes = useLiveQuery(() => banco.clientes.toArray(), [])
  const subPedidos = useLiveQuery(() => banco.sub_pedidos.toArray(), [])
  // Maior atualizado_em dos itens de cada sub-pedido: base da "ultima alteracao"
  // do cartao (pedido dela, 14/10).
  const itensPorSubPedido = useLiveQuery(async () => {
    const mapa = new Map<string, string>()
    await banco.itens_pedido.each((i) => {
      const atual = mapa.get(i.sub_pedido_id)
      if (!atual || i.atualizado_em > atual) mapa.set(i.sub_pedido_id, i.atualizado_em)
    })
    return mapa
  }, [])

  const cliente = (idCliente: string) => clientes?.find((c) => c.id === idCliente)
  const nomeCliente = (idCliente: string) => {
    const c = cliente(idCliente)
    return c?.nome_fantasia || c?.razao_social || ''
  }
  const marcasDoPedido = (idPedido: string): SubPedido[] =>
    (subPedidos ?? []).filter((s) => s.pedido_id === idPedido)

  const totalDoPedido = (marcas: SubPedido[]) =>
    marcas
      .filter((s) => s.apagado_em === null && s.status !== 'cancelado')
      .reduce((soma, s) => soma + s.valor_total, 0)

  function aplicarMes(deslocamento: 0 | -1) {
    const hoje = new Date()
    const { de, ate } = limitesDoMes(hoje.getFullYear(), hoje.getMonth() + deslocamento)
    const rotuloMes = new Date(hoje.getFullYear(), hoje.getMonth() + deslocamento, 1)
      .toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
    setFiltro({ tipo: 'periodo', de, ate, rotulo: rotuloMes })
    setPainelAberto(false)
  }

  function aplicarPersonalizado() {
    if (!personalizadoDe || !personalizadoAte) return
    setFiltro({
      tipo: 'periodo',
      de: personalizadoDe,
      ate: personalizadoAte,
      rotulo: `${dataBRDoTexto(personalizadoDe)} a ${dataBRDoTexto(personalizadoAte)}`,
    })
    setPainelAberto(false)
  }

  // Todos os pedidos vivos, com estagio e total -- ANTES do filtro e da busca.
  // Os cartoes de resumo contam a partir daqui, pra nao mudar de numero so
  // porque ela filtrou ou digitou algo (eles sao o resumo GERAL).
  const todasAsLinhas = (pedidos ?? [])
    .map((pedido) => {
      const marcas = marcasDoPedido(pedido.id)
      return { pedido, estagio: estagioDoPedido(marcas), total: totalDoPedido(marcas) }
    })
    // Em "Todos", a ordem e a do ciclo da venda (decisao dela): primeiro o que
    // ainda pede acao, por ultimo o que ja fechou. Dentro do mesmo estagio, o
    // mais novo primeiro.
    .sort((a, b) => {
      const diferenca = ORDEM_ESTAGIO.indexOf(a.estagio) - ORDEM_ESTAGIO.indexOf(b.estagio)
      return diferenca !== 0 ? diferenca : b.pedido.criado_em.localeCompare(a.pedido.criado_em)
    })

  const buscaNormalizada = normalizarTexto(busca)
  const linhas = todasAsLinhas
    .filter((linha) => {
      if (filtro.tipo === 'todos') return true
      if (filtro.tipo === 'estagio') return linha.estagio === filtro.estagio
      if (filtro.tipo === 'estagios') return filtro.estagios.includes(linha.estagio)
      const dataDoPedido = dataISOLocal(new Date(linha.pedido.criado_em))
      return dataDoPedido >= filtro.de && dataDoPedido <= filtro.ate
    })
    .filter((linha) => {
      if (buscaNormalizada === '') return true
      const alvo = normalizarTexto(`${linha.pedido.numero} ${nomeCliente(linha.pedido.cliente_id)}`)
      return alvo.includes(buscaNormalizada)
    })
    .slice(0, 100)

  const contagem = (estagio: Estagio) =>
    todasAsLinhas.filter((l) => l.estagio === estagio).length

  return (
    <section className="p-4 space-y-3">
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
          <IconeBusca />
        </span>
        <input
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar por número ou cliente..."
          className="w-full border border-slate-300 rounded-full pl-10 pr-3 py-2"
        />
      </div>

      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-lg font-bold text-slate-800">Pedidos</h2>
          {/* So aparece com o filtro de PERIODO -- o de situacao ja se ve nos
              cartoes de baixo (o aro no cartao ativo). Pedido dela: o botao do
              filtro fica so com o icone, entao o periodo ativo precisa
              aparecer em algum lugar, senao ela esquece o que esta filtrado. */}
          {/* Tambem com 'estagios' (atalhos da tela Inicio): sem chip, ninguem sabe que esta filtrado. */}
          {(filtro.tipo === 'periodo' || filtro.tipo === 'estagios') && (
            <button
              type="button"
              onClick={() => setFiltro({ tipo: 'todos' })}
              className="text-xs text-teal-700 underline"
            >
              {filtro.rotulo} · limpar
            </button>
          )}
        </div>
        <div className="relative shrink-0">
          <button
            type="button"
            onClick={() => setPainelAberto((v) => !v)}
            aria-label="Filtrar pedidos"
            aria-expanded={painelAberto}
            className={
              filtro.tipo === 'todos'
                ? 'w-9 h-9 rounded-full border border-slate-300 text-slate-500 bg-white flex items-center justify-center'
                : 'w-9 h-9 rounded-full bg-teal-700 text-white flex items-center justify-center'
            }
          >
            <IconeFiltro />
          </button>

          {painelAberto && (
            <div className="absolute right-0 z-20 mt-2 w-72 bg-white rounded-xl shadow-lg border border-slate-200 p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex rounded-lg bg-slate-100 p-0.5 text-sm">
                  <button
                    type="button"
                    onClick={() => setAbaPainel('situacao')}
                    className={`px-3 py-1 rounded-md ${abaPainel === 'situacao' ? 'bg-white shadow-sm font-medium text-slate-800' : 'text-slate-500'}`}
                  >
                    Situação
                  </button>
                  <button
                    type="button"
                    onClick={() => setAbaPainel('periodo')}
                    className={`px-3 py-1 rounded-md ${abaPainel === 'periodo' ? 'bg-white shadow-sm font-medium text-slate-800' : 'text-slate-500'}`}
                  >
                    Período
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setPainelAberto(false)}
                  aria-label="Fechar filtro"
                  className="text-slate-400"
                >
                  <IconeX />
                </button>
              </div>

              {abaPainel === 'situacao' ? (
                <div className="space-y-0.5 max-h-56 overflow-y-auto">
                  <button
                    type="button"
                    onClick={() => { setFiltro({ tipo: 'todos' }); setPainelAberto(false) }}
                    className={`w-full text-left text-sm rounded-lg px-2 py-1.5 ${filtro.tipo === 'todos' ? 'bg-teal-50 text-teal-800 font-medium' : 'hover:bg-slate-50'}`}
                  >
                    Todos
                  </button>
                  {ORDEM_ESTAGIO.map((estagio) => (
                    <button
                      key={estagio}
                      type="button"
                      onClick={() => { setFiltro({ tipo: 'estagio', estagio }); setPainelAberto(false) }}
                      className={`w-full text-left text-sm rounded-lg px-2 py-1.5 ${
                        filtro.tipo === 'estagio' && filtro.estagio === estagio
                          ? 'bg-teal-50 text-teal-800 font-medium'
                          : 'hover:bg-slate-50'
                      }`}
                    >
                      {ROTULO_ESTAGIO[estagio]}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => aplicarMes(0)}
                    className="w-full text-left text-sm rounded-lg px-2 py-1.5 hover:bg-slate-50"
                  >
                    Mês vigente
                  </button>
                  <button
                    type="button"
                    onClick={() => aplicarMes(-1)}
                    className="w-full text-left text-sm rounded-lg px-2 py-1.5 hover:bg-slate-50"
                  >
                    Mês anterior
                  </button>
                  <div className="border-t border-slate-100 pt-2">
                    <p className="text-xs text-slate-500 mb-1">Personalizado</p>
                    <div className="flex items-center gap-1.5">
                      <input
                        type="date"
                        aria-label="Data inicial"
                        value={personalizadoDe}
                        onChange={(e) => setPersonalizadoDe(e.target.value)}
                        className="flex-1 min-w-0 border border-slate-300 rounded-lg px-1.5 py-1 text-xs"
                      />
                      <span className="text-slate-400 text-xs">a</span>
                      <input
                        type="date"
                        aria-label="Data final"
                        value={personalizadoAte}
                        onChange={(e) => setPersonalizadoAte(e.target.value)}
                        className="flex-1 min-w-0 border border-slate-300 rounded-lg px-1.5 py-1 text-xs"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={aplicarPersonalizado}
                      disabled={!personalizadoDe || !personalizadoAte}
                      className="w-full mt-1.5 bg-teal-700 text-white rounded-lg py-1.5 text-sm disabled:opacity-50"
                    >
                      Aplicar
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Os 4 cartoes de resumo, menores (pedido dela, 17/09 -- "muito
          grande"). Clicaveis: tocar troca para o filtro de SITUACAO, do
          mesmo jeito que escolher no painel do icone. */}
      <div className="grid grid-cols-4 gap-1.5">
        <button
          type="button"
          onClick={() => setFiltro({ tipo: 'todos' })}
          className={`rounded-lg p-1.5 text-left ${filtro.tipo === 'todos' ? 'bg-teal-50 ring-2 ring-teal-600' : 'bg-slate-100'}`}
        >
          <span className="w-5 h-5 rounded-md bg-teal-700 text-white flex items-center justify-center">
            <IconeDocumento />
          </span>
          <span className="block text-[11px] text-slate-500 mt-0.5 leading-tight">Total</span>
          <span className="block text-sm font-bold text-slate-800">{todasAsLinhas.length}</span>
        </button>
        {ESTAGIOS_COM_CARTAO.map((estagio) => (
          <button
            key={estagio}
            type="button"
            onClick={() => setFiltro({ tipo: 'estagio', estagio })}
            className={`rounded-lg p-1.5 text-left ${CLASSE_ESTAGIO[estagio]} ${
              filtro.tipo === 'estagio' && filtro.estagio === estagio ? 'ring-2 ring-current' : ''
            }`}
          >
            <span className={`w-5 h-5 rounded-md text-white flex items-center justify-center ${COR_SOLIDA_ESTAGIO[estagio]}`}>
              <IconeDoEstagio estagio={estagio} />
            </span>
            <span className="block text-[11px] mt-0.5 leading-tight truncate">{ROTULO_ESTAGIO[estagio]}</span>
            <span className="block text-sm font-bold">{contagem(estagio)}</span>
          </button>
        ))}
      </div>

      {linhas.length === 0 && (
        <p className="text-sm text-slate-500">
          {busca !== ''
            ? 'Nenhum pedido encontrado.'
            : filtro.tipo === 'todos'
              ? 'Nenhum pedido ainda. Abra a Rota e toque em "Novo pedido" no cliente que você está visitando.'
              : filtro.tipo === 'estagio'
                ? `Nenhum pedido em "${ROTULO_ESTAGIO[filtro.estagio]}".`
                : `Nenhum pedido em "${filtro.rotulo}".`}
        </p>
      )}

      <ul className="space-y-2">
        {linhas.map(({ pedido, estagio, total }) => {
          return (
          <li key={pedido.id}>
            <Link
              to={`/pedidos/${pedido.id}`}
              className={`block rounded-xl shadow-sm p-3 border-l-4 ${CLASSE_CARTAO_ESTAGIO[estagio]}`}
            >
              <div className="flex items-center gap-2">
                <span className="w-8 h-8 shrink-0 rounded-lg bg-white/70 text-slate-500 flex items-center justify-center">
                  <IconeDocumento />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-slate-800">{pedido.numero}</span>
                    <span className="text-sm font-bold text-slate-700 shrink-0">{formatarMoeda(total)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    {/* Escuro o bastante para continuar legivel sobre o verde e o
                        vermelho fortes do pago e do cancelado. So o nome --
                        pedido dela em 17/09, sem o CNPJ que o mockup tinha. */}
                    <span className="text-xs text-slate-700 truncate">
                      {nomeCliente(pedido.cliente_id)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                      <IconeCalendario /> {formatarData(new Date(ultimaAlteracaoDoPedido(pedido, subPedidos ?? [], itensPorSubPedido ?? new Map())))}
                    </span>
                    {/* Circulo SOLIDO com o icone + pilula clara com o rotulo:
                        a mesma cor do cartao de resumo, so que aqui em par. */}
                    <span className="inline-flex items-center gap-1 shrink-0">
                      <span className={`w-4 h-4 rounded-full text-white flex items-center justify-center ${COR_SOLIDA_ESTAGIO[estagio]}`}>
                        <IconeDoEstagio estagio={estagio} />
                      </span>
                      <span className={`text-xs rounded-full px-2 py-0.5 ${CLASSE_ESTAGIO[estagio]}`}>
                        {ROTULO_ESTAGIO[estagio]}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          </li>
          )
        })}
      </ul>
    </section>
  )
}
